import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import ConfirmationModal from '../common/ConfirmationModal';

// Group-scoped permissions a delegated Supplier Admin can receive. Must match the backend list.
const PERMISSIONS = [
  { key: 'members', label: 'Members & invitations' },
  { key: 'settings', label: 'Edit group settings' },
];

const idOf = (v) => String(v?._id ?? v);
const labelOf = (u, unknown) => u?.username || u?.email || unknown;

// Shows the primary organizer and delegated Supplier Admins of one group, and lets the primary
// organizer (or a superadmin) manage them. The backend enforces the same rules.
export default function GroupRolesPanel({ group, currentUser, onChanged }) {
  const nameOf = (u) => labelOf(u, t('groupRoles.unknownUser'));
  const { t } = useTranslation();
  const [promoteId, setPromoteId] = useState('');
  const [promotePerms, setPromotePerms] = useState(['members']);
  const [transferId, setTransferId] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null); // delegate whose last permission was unchecked

  const primaryId = group.createdBy ? idOf(group.createdBy) : null;
  const canManage = currentUser?.role === 'superadmin' || (!!primaryId && currentUser?.userId === primaryId);
  const delegates = group.delegates || [];
  const delegateIds = new Set(delegates.map((d) => idOf(d.user)));
  const members = group.members || [];
  const promoteOptions = members.filter((m) => idOf(m) !== primaryId && !delegateIds.has(idOf(m)));
  const transferOptions = members.filter((m) => idOf(m) !== primaryId);

  // Returns true when the request succeeded; on failure the caller keeps its edits for retry
  const run = async (request, successMessage) => {
    setBusy(true);
    try {
      await request();
      toast.success(successMessage);
      onChanged();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || t('groupRoles.actionFailed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const setDelegatePerms = (userId, permissions, successMessage) =>
    run(() => api.patch(`/api/groups/${group._id}/delegates`, { userId, permissions }), successMessage);

  // Latest saved permissions per delegate (userId -> permissions), shown until the list reloads
  const [drafts, setDrafts] = useState({});
  const currentPerms = (d) => drafts[idOf(d.user)] ?? (d.permissions || []);
  const discard = (d) => setDrafts((prev) => { const next = { ...prev }; delete next[idOf(d.user)]; return next; });

  // Auto-save: the checkbox updates at once and saves in the background. On failure it reverts.
  const togglePerm = async (d, perm) => {
    const cur = currentPerms(d);
    const next = cur.includes(perm) ? cur.filter((p) => p !== perm) : [...cur, perm];
    // Removing every permission revokes the delegation, so confirm first (cancel saves nothing)
    if (next.length === 0) return setPendingRevoke(d);
    const id = idOf(d.user);
    setDrafts((prev) => ({ ...prev, [id]: next }));
    try {
      await api.patch(`/api/groups/${group._id}/delegates`, { userId: id, permissions: next });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('groupRoles.permRevertFailed'));
      setDrafts((prev) => ({ ...prev, [id]: cur }));
    }
  };

  // Revoking removes the row, so this one reloads the list
  const saveDelegate = async (d, permissions) => {
    const ok = await setDelegatePerms(idOf(d.user), permissions, t('groupRoles.delegationRevoked'));
    if (ok) discard(d);
  };

  const promote = () => {
    if (!promoteId) return toast.error(t('groupRoles.selectMember'));
    if (!promotePerms.length) return toast.error(t('groupRoles.choosePermission'));
    setDelegatePerms(promoteId, promotePerms, t('groupRoles.delegateAdded'));
    setPromoteId('');
  };

  const transfer = () => {
    if (!transferId) return;
    const target = members.find((m) => idOf(m) === transferId);
    const ok = window.confirm(
      t('groupRoles.transferConfirm', { name: nameOf(target), group: group.name })
    );
    if (!ok) return;
    run(() => api.patch(`/api/groups/${group._id}/owner`, { userId: transferId }), t('groupRoles.ownerUpdated'));
    setTransferId('');
  };

  return (
    <div className="mt-2 pt-2 border-t text-sm space-y-2">
      <ConfirmationModal
        isOpen={!!pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        onConfirm={() => {
          const delegate = pendingRevoke;
          setPendingRevoke(null);
          saveDelegate(delegate, []);
        }}
        title={t('groupRoles.revokeTitle')}
        message={t('groupRoles.revokeMessage')}
        confirmLabel={t('groupRoles.revokeConfirm')}
        cancelLabel={t('groupRoles.keepAccess')}
      />
      <div>
        <span className="font-semibold">{t('groupRoles.primaryOrganizer')}</span>{' '}
        {primaryId
          ? <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">{nameOf(group.createdBy)}</span>
          : <span className="italic text-gray-500">{t('groupRoles.notSet')}</span>}
      </div>

      {delegates.length > 0 && (
        <div className="space-y-1">
          <div className="font-semibold">{t('groupRoles.delegatedAdmins')}</div>
          {delegates.map((d) => (
            <div key={idOf(d.user)} className="flex flex-wrap items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800">{nameOf(d.user)}</span>
              {PERMISSIONS.map((p) => (
                <label key={p.key} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    disabled={!canManage || busy}
                    checked={currentPerms(d).includes(p.key)}
                    onChange={() => togglePerm(d, p.key)}
                  />
                  {t('groupRoles.perm_' + p.key)}
                </label>
              ))}
              {canManage && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setPendingRevoke(d)}
                  className="text-red-600 hover:underline"
                >
                  {t('groupRoles.revoke')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <select value={promoteId} onChange={(e) => setPromoteId(e.target.value)} className="border rounded px-2 py-1">
              <option value="">{t('groupRoles.promoteSelect')}</option>
              {promoteOptions.map((m) => <option key={idOf(m)} value={idOf(m)}>{nameOf(m)}</option>)}
            </select>
            {PERMISSIONS.map((p) => (
              <label key={p.key} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={promotePerms.includes(p.key)}
                  onChange={() => setPromotePerms((prev) => (prev.includes(p.key) ? prev.filter((x) => x !== p.key) : [...prev, p.key]))}
                />
                {t('groupRoles.perm_' + p.key)}
              </label>
            ))}
            <button
              type="button"
              disabled={busy || !promoteId}
              onClick={promote}
              className="bg-green-600 text-white px-2 py-1 rounded disabled:opacity-50"
            >
              {t('groupRoles.makeDelegate')}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select value={transferId} onChange={(e) => setTransferId(e.target.value)} className="border rounded px-2 py-1">
              <option value="">{t('groupRoles.transferSelect')}</option>
              {transferOptions.map((m) => <option key={idOf(m)} value={idOf(m)}>{nameOf(m)}</option>)}
            </select>
            <button
              type="button"
              disabled={busy || !transferId}
              onClick={transfer}
              className="bg-amber-600 text-white px-2 py-1 rounded disabled:opacity-50"
            >
              {t('groupRoles.transferOwnership')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
